import type { Content, ItemDef, Modifier, Slot, UpgradeDef } from '@gra/content';
import { nextRandom } from './rng';
import type { GameState } from './types';

/**
 * Profil gracza — wszystko, co przetrwa między wyprawami: bohater, ekwipunek, waluta, liczniki gachy.
 * Zwykły obiekt JSON, który klient zapisuje w localStorage (a docelowo na serwerze).
 */
export interface ItemInstance {
  uid: number;
  itemId: string;
  rarity: string;
  /** Rośnie po wylosowaniu duplikatu. */
  level: number;
}

export type Loadout = Partial<Record<Slot, number>>;

export interface Profile {
  version: 2;
  rng: number;
  nextUid: number;
  classId: string;
  currency: number;
  /** Złoto z wypraw (monety ze skrzyń i potworów) — na wzmacnianie przedmiotów i drzewko rozwoju. */
  gold: number;
  /** Rasa i płeć bohatera (wygląd i cechy rasowe). */
  raceId: string;
  gender: 'm' | 'k';
  /** Drzewko rozwoju w obozie: id ulepszenia → wykupiona ranga. */
  upgrades: Record<string, number>;
  inventory: ItemInstance[];
  /** Założony sprzęt osobno dla każdej klasy: classId → slot → uid. */
  equipped: Record<string, Loadout>;
  pity: { sinceEpic: number; sinceLegendary: number };
  stats: ProfileStats;
  /** Odłamki z duplikatów ponad maksymalny poziom — na wzmacnianie i sklepik banera. */
  shards: number;
  /** Osiągnięcia: id → „done” (nagroda czeka na odbiór) albo „claimed”. */
  achievements: Record<string, 'done' | 'claimed'>;
  /** Wybrany tytuł i ramka portretu: id osiągnięcia, które je dało (null = brak). */
  title: string | null;
  frame: string | null;
}

export interface ProfileStats {
  runs: number;
  bestTime: number;
  totalKills: number;
  pulls: number;
  /** Pokonani bossowie: id wroga → ile razy. */
  bossKills: Record<string, number>;
  /** Najdłuższe przetrwanie w strefie (s). */
  zoneBest: Record<string, number>;
  /** Klasy, którymi pokonano bossa końcowego strefy. */
  clears: Record<string, string[]>;
  /** Podniesione przedmioty wyprawy łącznie. */
  relics: number;
  /** Złoto zebrane na wyprawach łącznie. */
  goldEarned: number;
  /** Najwyższy poziom osiągnięty na jednej wyprawie. */
  bestLevel: number;
}

export function emptyStats(): ProfileStats {
  return { runs: 0, bestTime: 0, totalKills: 0, pulls: 0, bossKills: {}, zoneBest: {}, clears: {}, relics: 0, goldEarned: 0, bestLevel: 0 };
}

export interface PullResult {
  item: ItemInstance;
  /** true = duplikat podniósł poziom istniejącego przedmiotu. */
  upgraded: boolean;
  /** Duplikat na maksymalnym poziomie: zamiast przedmiotu tyle odłamków. */
  shards?: number;
  /** Przedmiot wyróżniony na banerze. */
  featured?: boolean;
}

export function createProfile(content: Content, classId: string, seed: number): Profile {
  const cls = content.classes[classId];
  if (!cls) throw new Error(`Nieznana klasa: ${classId}`);
  const profile: Profile = {
    version: 2,
    rng: seed | 0,
    nextUid: 1,
    classId,
    currency: content.game.gacha.startingCurrency,
    gold: 0,
    upgrades: {},
    raceId: Object.keys(content.races)[0]!,
    gender: 'm',
    inventory: [],
    equipped: {},
    pity: { sinceEpic: 0, sinceLegendary: 0 },
    stats: emptyStats(),
    shards: 0,
    achievements: {},
    title: null,
    frame: null,
  };
  giveStartingItems(content, profile, classId);
  return profile;
}

/**
 * Przedmioty startowe klasy: dodaje brakujące do ekwipunku, ale ich nie zakłada —
 * bohater zaczyna z pustymi rękami, a gracz sam wybiera, co nosi (atak i tak pochodzi z klasy).
 */
function giveStartingItems(content: Content, profile: Profile, classId: string): void {
  const cls = content.classes[classId]!;
  profile.equipped[classId] ??= {};
  for (const itemId of cls.startingItems) {
    if (!profile.inventory.some((i) => i.itemId === itemId)) addItem(profile, itemId, content.rarityOrder[0]!);
  }
}

/** Założony sprzęt aktualnej klasy. */
export function currentLoadout(profile: Profile): Loadout {
  return (profile.equipped[profile.classId] ??= {});
}

export function canEquip(content: Content, profile: Profile, itemId: string): boolean {
  return content.items[itemId]?.classes.includes(profile.classId) ?? false;
}

function addItem(profile: Profile, itemId: string, rarity: string): ItemInstance {
  const inst: ItemInstance = { uid: profile.nextUid++, itemId, rarity, level: 1 };
  profile.inventory.push(inst);
  return inst;
}

export function findItem(profile: Profile, uid: number): ItemInstance | undefined {
  return profile.inventory.find((i) => i.uid === uid);
}

export function equippedItems(content: Content, profile: Profile): { slot: Slot; inst: ItemInstance; def: ItemDef }[] {
  const out: { slot: Slot; inst: ItemInstance; def: ItemDef }[] = [];
  for (const [slot, uid] of Object.entries(currentLoadout(profile)) as [Slot, number][]) {
    const inst = findItem(profile, uid);
    const def = inst && content.items[inst.itemId];
    if (inst && def) out.push({ slot, inst, def });
  }
  return out;
}

/** Modyfikatory jednego przedmiotu po uwzględnieniu rzadkości i poziomu. */
export function itemModifiers(content: Content, inst: ItemInstance): Modifier[] {
  const def = content.items[inst.itemId];
  const rarity = content.rarities[inst.rarity];
  if (!def || !rarity) return [];
  const k = rarity.statMultiplier * (1 + content.game.gacha.levelBonus * (inst.level - 1));
  return def.modifiers.map((m) => ({ ...m, value: m.value * k }));
}

export function equipmentModifiers(content: Content, profile: Profile): Modifier[] {
  return equippedItems(content, profile).flatMap(({ inst }) => itemModifiers(content, inst));
}

/** Prosta miara siły przedmiotu do sortowania i porównań w UI. */
export function itemPower(content: Content, inst: ItemInstance): number {
  const rarity = content.rarities[inst.rarity];
  if (!rarity) return 0;
  return Math.round(100 * rarity.statMultiplier * (1 + content.game.gacha.levelBonus * (inst.level - 1)));
}

export function equip(content: Content, profile: Profile, uid: number): void {
  equipFor(content, profile, profile.classId, uid);
}

function equipFor(content: Content, profile: Profile, classId: string, uid: number): void {
  const inst = findItem(profile, uid);
  const def = inst && content.items[inst.itemId];
  if (!inst || !def) throw new Error(`Brak przedmiotu ${uid}`);
  if (!def.classes.includes(classId)) throw new Error(`${def.name} nie jest dla tej klasy`);
  const loadout = (profile.equipped[classId] ??= {});
  loadout[def.slot] = uid;
  // Broń dwuręczna i druga ręka wykluczają się nawzajem.
  if (def.twoHanded) delete loadout.offhand;
  if (def.slot === 'offhand') {
    const weapon = loadout.weapon !== undefined ? findItem(profile, loadout.weapon) : undefined;
    if (weapon && content.items[weapon.itemId]?.twoHanded) delete loadout.weapon;
  }
}

export function unequip(profile: Profile, slot: Slot): void {
  delete currentLoadout(profile)[slot];
}

/** Zmienia klasę bohatera. Każda klasa ma własny założony sprzęt; brakujące przedmioty startowe trafiają do ekwipunku. */
export function setClass(content: Content, profile: Profile, classId: string): void {
  if (!content.classes[classId]) throw new Error(`Nieznana klasa: ${classId}`);
  profile.classId = classId;
  giveStartingItems(content, profile, classId);
}

function rollRarity(content: Content, profile: Profile): string {
  const { pityEpic, pityLegendary } = content.game.gacha;
  const order = content.rarityOrder;
  const legendary = order[order.length - 1]!;
  const epic = order[order.length - 2] ?? legendary;
  profile.pity.sinceEpic += 1;
  profile.pity.sinceLegendary += 1;

  let rarity: string;
  if (profile.pity.sinceLegendary >= pityLegendary) {
    rarity = legendary;
  } else {
    const total = order.reduce((sum, id) => sum + content.rarities[id]!.weight, 0);
    let roll = nextRandom(profile) * total;
    rarity = order[0]!;
    for (const id of order) {
      roll -= content.rarities[id]!.weight;
      if (roll <= 0) {
        rarity = id;
        break;
      }
    }
    // Gwarancja epickiego: podbijamy do epickiego, jeśli wylosowało się coś słabszego.
    if (profile.pity.sinceEpic >= pityEpic && order.indexOf(rarity) < order.indexOf(epic)) rarity = epic;
  }

  if (order.indexOf(rarity) >= order.indexOf(epic)) profile.pity.sinceEpic = 0;
  if (rarity === legendary) profile.pity.sinceLegendary = 0;
  return rarity;
}

export function canPull(content: Content, profile: Profile, count = 1): boolean {
  return profile.currency >= content.game.gacha.pullCost * count;
}

/**
 * Wyróżnione przedmioty banera w danej chwili (wszystkich klas). Zestaw zmienia się co `rotationDays` dni;
 * czas podaje klient (silnik nie czyta zegara), więc wynik jest deterministyczny.
 */
export function bannerFeatured(content: Content, bannerId: string, now: number): string[] {
  const banner = content.banners[bannerId];
  if (!banner || banner.rotation.length === 0) return [];
  const period = Math.floor(now / (banner.rotationDays * 86_400_000));
  return banner.rotation[((period % banner.rotation.length) + banner.rotation.length) % banner.rotation.length]!;
}

/** Ms do następnej zmiany zestawu na banerze (null = baner bez rotacji). */
export function bannerRotationLeft(content: Content, bannerId: string, now: number): number | null {
  const banner = content.banners[bannerId];
  if (!banner || banner.rotation.length === 0) return null;
  const len = banner.rotationDays * 86_400_000;
  return len - (((now % len) + len) % len);
}

export interface PullOptions {
  /** Id banera (domyślnie pierwszy, stały). */
  banner?: string;
  /** Czas klienta w ms — wybiera zestaw wyróżnionych przedmiotów. */
  now?: number;
}

export function pull(content: Content, profile: Profile, count = 1, opts: PullOptions = {}): PullResult[] {
  if (!canPull(content, profile, count)) throw new Error('Za mało waluty');
  const banner = content.banners[opts.banner ?? Object.keys(content.banners)[0]!];
  if (!banner) throw new Error(`Nieznany baner: ${opts.banner}`);
  // Przywołanie losuje tylko sprzęt dla aktualnej klasy.
  const items = Object.values(content.items).filter((i) => i.classes.includes(profile.classId));
  const featured = bannerFeatured(content, banner.id, opts.now ?? 0).filter((id) => content.items[id]?.classes.includes(profile.classId));
  const minFeatured = content.rarityOrder.indexOf(banner.featuredMinRarity ?? content.rarityOrder[0]!);
  const results: PullResult[] = [];
  for (let i = 0; i < count; i++) {
    profile.currency -= content.game.gacha.pullCost;
    profile.stats.pulls += 1;
    const rarity = rollRarity(content, profile);
    // Na banerze wyróżnionym część trafień od progu rzadkości to przedmiot z zestawu tygodnia.
    const isFeatured = featured.length > 0 && content.rarityOrder.indexOf(rarity) >= minFeatured && nextRandom(profile) < banner.featuredRate;
    const pool = isFeatured ? featured : items.map((d) => d.id);
    const itemId = pool[Math.floor(nextRandom(profile) * pool.length)]!;
    const result = grantItem(content, profile, itemId, rarity);
    if (isFeatured || featured.includes(itemId)) result.featured = true;
    results.push(result);
  }
  return results;
}

/**
 * Dodaje przedmiot jak z przywołania: duplikat tej samej rzadkości podnosi poziom istniejącego,
 * a gdy ten ma już maksymalny poziom — zamienia się w odłamki.
 */
function grantItem(content: Content, profile: Profile, itemId: string, rarity: string): PullResult {
  const existing = profile.inventory.find((it) => it.itemId === itemId && it.rarity === rarity);
  if (existing && existing.level < content.game.gacha.maxLevel) {
    existing.level += 1;
    return { item: existing, upgraded: true };
  }
  if (existing) {
    const shards = content.game.gacha.shardsPerDuplicate[rarity] ?? 0;
    profile.shards += shards;
    return { item: existing, upgraded: false, shards };
  }
  return { item: addItem(profile, itemId, rarity), upgraded: false };
}

/** Koszt wzmocnienia odłamkami (null = maksymalny poziom). */
export function shardUpgradeCost(content: Content, inst: ItemInstance): number | null {
  if (inst.level >= content.game.gacha.maxLevel) return null;
  return content.game.gacha.shardUpgradeCost * inst.level;
}

export function upgradeItemWithShards(content: Content, profile: Profile, uid: number): void {
  const inst = findItem(profile, uid);
  if (!inst) throw new Error(`Brak przedmiotu ${uid}`);
  const cost = shardUpgradeCost(content, inst);
  if (cost === null) throw new Error('Przedmiot ma już maksymalny poziom');
  if (profile.shards < cost) throw new Error('Za mało odłamków');
  profile.shards -= cost;
  inst.level += 1;
}

/** Sklepik banera: wyróżniony przedmiot z bieżącego zestawu za odłamki. */
export function buyFeatured(content: Content, profile: Profile, bannerId: string, itemId: string, now: number): PullResult {
  const banner = content.banners[bannerId];
  if (!banner?.shop) throw new Error('Ten baner nie ma sklepiku');
  if (!bannerFeatured(content, bannerId, now).includes(itemId)) throw new Error('Tego przedmiotu nie ma w zestawie tygodnia');
  if (!content.items[itemId]?.classes.includes(profile.classId)) throw new Error('Ten przedmiot nie jest dla tej klasy');
  if (profile.shards < banner.shop.cost) throw new Error('Za mało odłamków');
  profile.shards -= banner.shop.cost;
  return grantItem(content, profile, itemId, banner.shop.rarity);
}

/** Łupy z wyprawy trafiają do profilu: złoto i znaleziony sprzęt. */
export function claimLoot(content: Content, profile: Profile, state: GameState): { gold: number; items: PullResult[] } {
  profile.gold += state.gold;
  const items = state.loot.filter((l) => content.items[l.itemId] && content.rarities[l.rarity]).map((l) => grantItem(content, profile, l.itemId, l.rarity));
  return { gold: state.gold, items };
}

/** Koszt wzmocnienia przedmiotu złotem (null = maksymalny poziom). */
export function upgradeCost(content: Content, inst: ItemInstance): number | null {
  if (inst.level >= content.game.gacha.maxLevel) return null;
  return content.game.loot.upgradeCost * inst.level;
}

/** Wzmocnienie przedmiotu o poziom za złoto. */
export function upgradeItem(content: Content, profile: Profile, uid: number): void {
  const inst = findItem(profile, uid);
  if (!inst) throw new Error(`Brak przedmiotu ${uid}`);
  const cost = upgradeCost(content, inst);
  if (cost === null) throw new Error('Przedmiot ma już maksymalny poziom');
  if (profile.gold < cost) throw new Error('Za mało złota');
  profile.gold -= cost;
  inst.level += 1;
}

// ---------- drzewko rozwoju (stałe ulepszenia za złoto) ----------

/** Ile rang łącznie wykupiono w drzewku. */
export function spentUpgradeRanks(profile: Profile): number {
  return Object.values(profile.upgrades).reduce((sum, r) => sum + r, 0);
}

/** Talent widoczny dla bieżącej klasy (węzły gałęzi „klasa” należą do jednej klasy). */
export function upgradeVisible(profile: Profile, def: UpgradeDef): boolean {
  return !def.classes || def.classes.includes(profile.classId);
}

/** Punkty wydane w jednej gałęzi drzewka (od nich zależy odblokowanie kolejnych rzędów). Liczą się węzły bieżącej klasy. */
export function branchPoints(content: Content, profile: Profile, branch: string): number {
  let sum = 0;
  for (const [id, rank] of Object.entries(profile.upgrades)) {
    const def = content.upgrades[id];
    if (def?.branch === branch && upgradeVisible(profile, def)) sum += rank;
  }
  return sum;
}

/** Talent dostępny: rząd odblokowany punktami w gałęzi, a poprzednik (linia na drzewku) ma choć jedną rangę. */
export function isUpgradeUnlocked(content: Content, profile: Profile, def: UpgradeDef): boolean {
  if (!upgradeVisible(profile, def)) return false;
  if (branchPoints(content, profile, def.branch) < def.requires) return false;
  return !def.after || (profile.upgrades[def.after] ?? 0) > 0;
}

/** Koszt następnej rangi (null = maksymalna ranga). */
export function upgradeRankCost(profile: Profile, def: UpgradeDef): number | null {
  const rank = profile.upgrades[def.id] ?? 0;
  if (rank >= def.maxRank) return null;
  return Math.round(def.cost.base * def.cost.growth ** rank);
}

export function buyUpgrade(content: Content, profile: Profile, id: string): void {
  const def = content.upgrades[id];
  if (!def) throw new Error(`Nieznane ulepszenie: ${id}`);
  if (!isUpgradeUnlocked(content, profile, def)) throw new Error(`${def.name} jest jeszcze zablokowane`);
  const cost = upgradeRankCost(profile, def);
  if (cost === null) throw new Error(`${def.name} ma już maksymalną rangę`);
  if (profile.gold < cost) throw new Error('Za mało złota');
  profile.gold -= cost;
  profile.upgrades[id] = (profile.upgrades[id] ?? 0) + 1;
}

/** Modyfikatory z drzewka (wartość × ranga) — dokładane do ekwipunku przy starcie wyprawy. */
export function upgradeModifiers(content: Content, profile: Profile): Modifier[] {
  const out: Modifier[] = [];
  for (const [id, rank] of Object.entries(profile.upgrades)) {
    const def = content.upgrades[id];
    // Talenty innej klasy czekają, aż bohater wróci do tamtej klasy.
    if (!def || rank <= 0 || !upgradeVisible(profile, def)) continue;
    for (const m of def.modifiers) out.push({ ...m, value: m.value * rank });
  }
  return out;
}

/** Zmiana rasy albo płci bohatera — darmowa, w każdej chwili w obozie. */
export function setRace(content: Content, profile: Profile, raceId: string, gender: 'm' | 'k' = profile.gender): void {
  if (!content.races[raceId]) throw new Error(`Nieznana rasa: ${raceId}`);
  profile.raceId = raceId;
  profile.gender = gender;
}

/** Nagroda za wyprawę: waluta za zabójstwa i czas przetrwania. Zwraca przyznaną kwotę. */
export function rewardRun(content: Content, profile: Profile, state: GameState): number {
  const g = content.game.gacha;
  const zone = content.zones[state.zoneId];
  // Zwycięstwo (pokonany boss końcowy) daje półtora raza więcej.
  const reward = Math.floor((state.kills * g.currencyPerKill + (state.time / 60) * g.currencyPerMinute) * (zone?.rewardMultiplier ?? 1) * (state.won ? 1.5 : 1));
  profile.currency += reward;
  const st = profile.stats;
  st.runs += 1;
  st.totalKills += state.kills;
  st.bestTime = Math.max(st.bestTime, state.time);
  st.zoneBest[state.zoneId] = Math.max(st.zoneBest[state.zoneId] ?? 0, state.time);
  for (const id of state.defeatedBosses) st.bossKills[id] = (st.bossKills[id] ?? 0) + 1;
  if (state.won) {
    const clears = (st.clears[state.zoneId] ??= []);
    if (!clears.includes(state.classId)) clears.push(state.classId);
  }
  st.relics += state.relics.length;
  st.goldEarned += state.gold;
  st.bestLevel = Math.max(st.bestLevel, state.level);
  return reward;
}
